import { memo } from "react";
import { Platform } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import Animated from "react-native-reanimated";
import { PressableScale } from "pressto";
import { View, Text } from "@src/components";
import { Checkbox } from "@src/components/Checkbox";
import { LegacySnapshotImage } from "./LegacySnapshotImage";
import { getCameraKeyString, getCameraDisplayName, formatCameraLabel } from "@src/utils/key-parser";
import type { CameraApiResponse } from "@src/features/cameras";
import { toSentenceCase } from "@visioni/a3s-ui/utils";

interface CameraThumbnailCardProps {
  camera: CameraApiResponse;
  isSelected: boolean;
  onToggle: () => void;
  isSaving?: boolean;
}

const SNAPSHOT_HEIGHT = 85;
const CARD_CONTENT_HEIGHT = 76;

export const CameraThumbnailCard = memo(({ camera, isSelected, onToggle, isSaving }: CameraThumbnailCardProps) => {
  const { theme } = useUnistyles();
  const cameraKey = getCameraKeyString(camera);
  const displayName = camera.name || getCameraDisplayName(cameraKey);
  const subtitleName = formatCameraLabel(cameraKey);

  return (
    <PressableScale onPress={onToggle} enabled={!isSaving}>
      <Animated.View
        style={[
          styles.cardBorder,
          isSelected && { borderColor: theme.colors.interactive.buttons.default_background },
          !isSelected && { borderColor: theme.colors.borderCard },
        ]}
      >
        <View style={styles.cardContent}>
          <View style={styles.thumbnailContainer}>
            <LegacySnapshotImage
              cameraKey={cameraKey}
              thumbnailUrl={camera.snapshot?.url}
              capturedAt={camera.snapshot?.capturedAt}
              style={styles.thumbnail}
            />
            <View style={styles.checkboxOverlay}>
              <Checkbox checked={isSelected} onToggle={onToggle} />
            </View>
          </View>
          <View p="xl" style={{ height: CARD_CONTENT_HEIGHT }}>
            <Text textStyle="xs/medium" color="text" mt="xs" numberOfLines={1}>
              {toSentenceCase(displayName)}
            </Text>
            <Text textStyle="xxs/regular" color="textMuted" numberOfLines={1}>
              {subtitleName}
            </Text>
          </View>
        </View>
      </Animated.View>
    </PressableScale>
  );
});

/** Empty placeholder card shown during the ghost/skeleton transition. */
export const CameraSkeletonCard = memo(() => (
  <View style={[styles.cardBorder, styles.skeletonCard]}>
    <View style={styles.skeletonThumbnail} />
    <View style={styles.skeletonText} />
  </View>
));

export const CameraGhostCard = memo(() => (
  <View style={[styles.cardBorder, styles.ghostCard, styles.ghostCardBorder]}>
    <View style={[styles.cardContent, styles.ghostCard]}>
      <View style={[styles.thumbnailContainer, styles.ghostCard]} />
      <View p="xl" style={{ height: CARD_CONTENT_HEIGHT }}>
        <View style={styles.skeletonText} />
      </View>
    </View>
  </View>
));

export const cameraThumbnailGridStyles = StyleSheet.create(() => ({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  gridItem: {
    flexBasis: "31%",
    flexGrow: 1,
    maxWidth: "32%",
    // TODO: revisit minWidth for native once mobile manage-routines layout is refined
    ...Platform.select({ web: { minWidth: 140 } }),
  },
}));

const styles = StyleSheet.create((theme) => ({
  cardBorder: {
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    overflow: "hidden",
    borderColor: theme.colors.borderCard,
  },
  cardContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.colors.surfaceCard,
  },
  thumbnailContainer: {
    height: SNAPSHOT_HEIGHT,
    width: "100%",
    borderRadius: theme.spacing.md,
    backgroundColor: theme.colors.borderCard,
  },
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  checkboxOverlay: {
    position: "absolute",
    top: theme.spacing.sm,
    right: theme.spacing.sm,
  },
  skeletonCard: {
    backgroundColor: theme.colors.surfaceCard,
    padding: theme.spacing.lg,
    gap: theme.spacing.lg,
    height: SNAPSHOT_HEIGHT + CARD_CONTENT_HEIGHT,
  },
  skeletonThumbnail: {
    height: SNAPSHOT_HEIGHT + 15 - theme.spacing.lg * 2,
    borderRadius: theme.spacing.sm,
    backgroundColor: theme.colors.borderCard,
  },
  skeletonText: {
    height: CARD_CONTENT_HEIGHT - 15 - theme.spacing.lg * 3,
    width: "60%",
    borderRadius: theme.spacing.sm,
    backgroundColor: theme.colors.borderCard,
  },
  ghostCard: {
    backgroundColor: "transparent",
  },
  ghostCardBorder: {
    borderColor: theme.colors.borderCard,
  },
}));
